import { Skeleton } from '@/components/ui/skeleton'
import { TreeNode } from './TreeNode'

import { useShallow } from 'zustand/react/shallow'
import { useRef, useMemo, useEffect } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { useIsReadOnly } from '@/pages/CADPage/hooks/useIsReadOnly'
import { useDocumentPageQuery } from '@/pages/DocumentPage/queries'
import { useCADPageStore } from '@/state'

import type { RawAssemblyTree, ASMTreeNode } from '@/state'
import type { GLTFObject } from '@/lib/cad/GLTFObject'

const NODE_HEIGHT = 35
const OVERSCAN = 10

export const TreeNodes = ({
  tree,
  gltf,
}: {
  tree: RawAssemblyTree
  gltf: GLTFObject
}) => {
  const parentRef = useRef(null)
  const rowVirtualizer = useVirtualizer({
    count: tree.nodes.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => NODE_HEIGHT,

    useAnimationFrameWithResizeObserver: true,
    initialMeasurementsCache: [],
    overscan: OVERSCAN,
  })

  const expandedAssemblyTreeNodes = useCADPageStore(
    (state) => state.expandedAssemblyTreeNodes,
  )

  const virtualTreeNodes = useMemo(() => {
    const nodesById = new Map(tree.nodes.map((n) => [n.uuid, n]))
    const result: ASMTreeNode[] = []

    // Pre-order traversal will ensure the nodes remain
    // in the correct order
    function traverse(uuid: string) {
      const node = nodesById.get(uuid)
      if (!node) return

      // skip the root itself if you don’t want to show it
      if (uuid !== tree.root) {
        result.push(node)
      }

      // only descend if node is root or it’s expanded
      const isExpanded =
        uuid === tree.root || expandedAssemblyTreeNodes.includes(uuid)

      if (isExpanded && node.children) {
        node.children.forEach((childUUID) => {
          traverse(childUUID)
        })
      }
    }

    traverse(tree.root)
    return result
  }, [expandedAssemblyTreeNodes, tree.nodes, tree.root])

  const { isLoading: isDocumentLoading, data: docData } = useDocumentPageQuery()
  const {
    isLoading: isReadOnlyInfoLoading,
    data: { isReadOnly },
  } = useIsReadOnly()

  /**
   *
   * Handle scrolling tree node into view
   *
   * For example, when a user clicks a part in the
   * CAD model, we'll scroll the part into view within
   * the assembly tree.
   */

  const assemblyNodeToScrollTo = useCADPageStore(
    (state) => state.assemblyNodeToScrollTo,
  )
  const scrollAssemblyNodeIntoView = useCADPageStore(
    useShallow((state) => state.scrollAssemblyNodeIntoView),
  )

  useEffect(() => {
    if (assemblyNodeToScrollTo) {
      const uuidIndex = virtualTreeNodes.findIndex(
        (n) => n.uuid === assemblyNodeToScrollTo,
      )

      if (uuidIndex < 0) {
        return scrollAssemblyNodeIntoView(null)
      }

      rowVirtualizer.scrollToIndex(uuidIndex)

      requestAnimationFrame(() => {
        const el = document.getElementById(
          `tree-node-container-${assemblyNodeToScrollTo}`,
        )
        el?.scrollIntoView({ behavior: 'smooth' })
        el?.classList.add('bg-primary-10', 'border', '!border-primary-50')
        setTimeout(() => {
          el?.classList.remove('bg-primary-10', 'border', '!border-primary-50')
          scrollAssemblyNodeIntoView(null)
        }, 1500)
      })
    }

    return () => {
      scrollAssemblyNodeIntoView(null)
    }
  }, [
    assemblyNodeToScrollTo,
    rowVirtualizer,
    scrollAssemblyNodeIntoView,
    virtualTreeNodes,
  ])

  const isLoading = isDocumentLoading || isReadOnlyInfoLoading
  const documentType = docData.documentType || 'work_instructions'

  if (isLoading) {
    return <Skeleton />
  }

  return (
    <div
      ref={parentRef}
      style={{
        height: `100%`,
        overflowY: 'auto',
      }}
    >
      <div
        style={{
          height: `${virtualTreeNodes.length * NODE_HEIGHT}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {rowVirtualizer.getVirtualItems().map((virtualItem) => {
          const node = virtualTreeNodes[virtualItem.index]
          if (!node) {
            return null
          }
          return (
            <div
              key={virtualItem.key}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: `${virtualItem.size}px`,
                transform: `translateY(${virtualItem.start}px)`,
              }}
            >
              <TreeNode
                tree={tree}
                node={virtualTreeNodes[virtualItem.index]}
                gltf={gltf}
                level={virtualTreeNodes[virtualItem.index].depth || 0}
                isReadOnly={isReadOnly}
                documentType={documentType}
              />
            </div>
          )
        })}
      </div>
    </div>
  )
}
